import type { State } from 'src/replicant/State';
import { CareItemID } from '../care/care.ruleset';
import {
  energyRegenerationConfig,
  maxMinigameEnergy,
} from './inventory.ruleset';
import { GemsExchange } from '../data/GemsExchange.schema';
import gemsToPoints from '../data/exchange/GemsToPoints.json';
import gemsToEnergyM2 from '../data/exchange/GemsToEnergy_match_2.json';

/** Get common currency */
export function getPoints({ inventory }: State): number {
  return inventory.points;
}

/** Get free premium currency */
export function getFreeGems({ inventory }: State): number {
  return inventory.premium.free;
}

/** Get paid premium currency */
export function getPaidGems({ inventory }: State): number {
  return inventory.premium.paid;
}

/** Get premium currency */
export function getGems(state: State): number {
  return getFreeGems(state) + getPaidGems(state);
}

/** Get star currency */
export function getStars({ inventory }: State): number {
  return inventory.stars;
}

/** Get max energy for player. */
export function getMaxEnergy(_state: State): number {
  return maxMinigameEnergy.default;
}

/** Energy regeneration rate */
export function getEnergyRegenerationConfig(_state: State): {
  amount: number;
  time: number;
} {
  return energyRegenerationConfig.match2;
}

/** Get current energy amount at given time */
export function getEnergy(
  state: State,
  now: number,
  forceDefaultConfig = false,
): number {
  // Get current state.
  const { energy, energyRechargeStartTime } = state.inventory;

  // Get max energy.
  const maxEnergy = forceDefaultConfig
    ? maxMinigameEnergy.default
    : getMaxEnergy(state);

  // Check if we have more energy then max.
  if (energy >= maxEnergy) {
    // We may have gotten extra energy from somewhere. Don't lose it.
    return energy;
  }

  // How much time has passed since last energy regeneration.
  const duration = now - energyRechargeStartTime;

  // Get regeneration config.
  const { time, amount } = forceDefaultConfig
    ? energyRegenerationConfig.default
    : getEnergyRegenerationConfig(state);

  // How much energy should be regenerated.
  const regenerated = amount * Math.floor(duration / time);

  // Return current energy.
  return Math.min(regenerated + energy, maxEnergy);
}

/** Get time until energy is fully recharged. */
export function getTimeToFullEnergy(state: State, now: number): number {
  const maxEnergy = getMaxEnergy(state);
  const { time } = getEnergyRegenerationConfig(state);

  const timeSinceLastEnergy =
    (now - state.inventory.energyRechargeStartTime) % time;
  const timeToNextEnergy = time - timeSinceLastEnergy;

  const amountToRefill = maxEnergy - getEnergy(state, now) - 1;
  const timeToFull = time * amountToRefill;

  return Math.max(timeToNextEnergy + timeToFull, 0);
}

export function getCurrencies(
  state: State,
  now: number,
): {
  points: number;
  energy: number;
  freeGems: number;
  paidGems: number;
  stars: number;
} {
  return {
    points: getPoints(state),
    energy: getEnergy(state, now),
    freeGems: getFreeGems(state),
    paidGems: getPaidGems(state),
    stars: getStars(state),
  };
}

/** Get care items. */
export function getCareItems(state: State) {
  return state.inventory.care as Record<
    CareItemID,
    { count: number } | undefined
  >;
}

/** Returns true if energy is full */
export function isEnergyFull(state: State, now: number): boolean {
  return getEnergy(state, now) >= getMaxEnergy(state);
}

/** How many energy is needed to fill up to max */
export function getEnergyToFull(state: State, now: number): number {
  return Math.max(getMaxEnergy(state) - getEnergy(state, now), 0);
}

/**
 * Get gems exchange rate
 */
export function getGemsToPointsExchangeRates(): GemsExchange {
  return gemsToPoints;
}

/**
 * Get energy exchange rate
 */
export function getGemsToEnergyExchangeRates(_user: State): GemsExchange {
  return gemsToEnergyM2;
}

/**
 * Get gem value for energy amount.
 */
export function getEnergyGemValue(state: State, amount: number): number {
  const exchange = getGemsToEnergyExchangeRates(state);

  // Find closest value.
  let closest = exchange.findIndex((e) => e.items >= amount);

  // If cannot find closest, use last value.
  if (closest === -1) {
    closest = exchange.length - 1;
  }

  // If closest is not  exact match, use previous value and add a percentage of current value.
  if (exchange[closest].items !== amount) {
    const previous = exchange[closest - 1];
    const next = exchange[closest];
    const previousPrice = previous?.items ?? 0;
    const nextPrice = next?.items ?? 0;
    const percentage = (amount - previousPrice) / (nextPrice - previousPrice);

    const previousReward = (previous?.premium ?? 0) + (previous?.bonus ?? 0);
    const nextReward = (next?.premium ?? 0) + (next?.bonus ?? 0);
    return previousReward + percentage * (nextReward - previousReward);
  }

  // Return exact match.
  return exchange[closest].premium + exchange[closest].bonus;
}
